import type { PayloadAction } from "@reduxjs/toolkit";
import type { StoreKey } from "../../../myOrders/buckets";

/**
 * ⚠️ THIS IS v1's MODEL. My-orders v2 owned its reads from D13 and its mutations from D14
 * (docs/MY_ORDERS_V2_PLAN.md), so the bridge that used to fill `ordersV2Store` from this slice's
 * action types is gone: the ONLY thing v2 still reads here is `chatSupportUrlDetails`
 * (`myOrders/selectors.ts`). Everything else below serves v1 web until §8.2 deletes it.
 *
 * Shared parts of MyOrderSlice.
 *
 * WEB-ADOPTION PASS. Previously this held only the 43 reducers that were byte-identical in both
 * apps, with web's extra 17 stranded in `index.desktop.ts` where mobile could never reach them.
 * Those 17 — prepared-orders, the Zomato chat-support cluster, and six one-liners — are pure
 * state manipulation with no platform API in sight: DEMAND-DRIVEN divergence, so they are here
 * now and mobile gains them. `fetchOrderDataSuccess` came with them, because the 17 need the
 * state fields it writes.
 *
 * Adding a reducer does not by itself add a feature — mobile's saga and screens still have to
 * dispatch these. This is the foundation that makes that possible; an unused reducer costs
 * nothing at runtime.
 *
 * STILL AUTHORED PER PLATFORM, and each for a stated reason:
 *
 * - `resetMyOrders` — ✅ CONVERGED 2026-09-15 onto WEB's body (decision D1, plan §6, taken by
 *   default: the overview re-fetches anyway). Mobile used to clear `overviewOrdersInfo` here;
 *   it no longer does, so the shared my-orders View shows the same overview on both platforms.
 *   `MyOrderSlice.test.ts` pins it.
 * - `userLogout` / `supportLogout` extraReducers (web) — BLOCKED, not permanent. Those actions
 *   do not exist in `LoginSlice/index.native.ts`, so mobile cannot reference them. Both bodies
 *   are empty on web anyway, so converging them buys nothing today. Revisit when LoginSlice
 *   converges.
 * - `toggleAutoRefreshMode`, `updateNextAction`, `isAutoRefreshModeEnabled` (mobile) — mobile-only
 *   FEATURES, not divergent implementations of a shared one. Adoption runs web -> mobile; hoisting
 *   these would add dead state to web.
 *
 * ── NO FETCH FLAGS HERE (plan §10.2, then undone 2026-09-17) ───────────────────────────────
 * v1's nine `is<List>OrdersFetching` booleans had no reader in either app. §10.2 collapsed them
 * into one `fetching: StoreKey | null` + `listFetchFailed` because v2 then DERIVED its reads
 * from this slice (D9); D13 gave v2 its own store, whose `ordersV2Store.buckets[key].fetching`
 * is what `myOrders/selectors.selectFetching` reads — leaving the collapsed field with no
 * reader either, so it is gone too. The nine request/success action creators stay: they are
 * v1's dispatch surface in 20+ files. A request reducer therefore stores nothing.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling. Reducers
 * the apps wrote with no `action` param at all (`resetSearchOrders`) keep that shape: RTK
 * generates a no-payload creator for them today and that is the behaviour to preserve.
 */
export interface SharedMyOrderState {
  overviewOrdersInfo: {
    [key: string]: any;
  };
  newOrdersInfo: {
    [key: string]: any;
  };
  preparingOrdersInfo: {
    [key: string]: any;
  };
  processedOrdersInfo: {
    [key: string]: any;
  };
  dispatchOrdersInfo: {
    [key: string]: any;
  };
  deliveredOrdersInfo: {
    [key: string]: any;
  };
  cancelledOrdersInfo: {
    [key: string]: any;
  };
  openOrdersInfo: {
    [key: string]: any;
  };
  searchOrdersInfo: {
    [key: string]: any;
  };
  feedbackInfo: {
    [key: string]: any;
  };
  isOrderStatusUpdate: boolean;
  isFetchingOrderData: boolean;
  isItemSoldOut: boolean;
  isAggregatorUpdate: boolean;
  isFetchingHoldOrder: boolean;
  isFetchingCallBtn: boolean;
  isMyOrderSave: boolean;
  isSwiggyBtnCall: boolean;
  // Adopted from web with the 17 reducers below. Mobile gains these fields; nothing there
  // reads them yet, which is exactly what makes the adoption safe to land ahead of the UI.
  currentOrderData: any;
  newChatSupportMessage: Record<string, number>;
  chatSupportUrlDetails: Record<string, string>[];
  removedChatSupportId: string;
  chatSupportAccess: Record<string, string>[];
  preparedOrdersInfo: {
    [key: string]: any;
  };
}

export const sharedInitialState: SharedMyOrderState = {
  overviewOrdersInfo: {},
  newOrdersInfo: {},
  preparingOrdersInfo: {},
  processedOrdersInfo: {},
  dispatchOrdersInfo: {},
  deliveredOrdersInfo: {},
  cancelledOrdersInfo: {},
  openOrdersInfo: {},
  searchOrdersInfo: {},
  feedbackInfo: {},
  isOrderStatusUpdate: false,
  isFetchingOrderData: false,
  isItemSoldOut: false,
  isAggregatorUpdate: false,
  isFetchingHoldOrder: false,
  isFetchingCallBtn: false,
  isMyOrderSave: false,
  isSwiggyBtnCall: false,
  currentOrderData: {},
  newChatSupportMessage: {},
  chatSupportUrlDetails: [],
  removedChatSupportId: "",
  chatSupportAccess: [],
  preparedOrdersInfo: {},
};

// The request/success pair every order list shares. All nine request reducers ARE this one
// no-op — the action exists only for the saga to `takeLatest` on — but its `action` param MUST
// still be typed: without it RTK infers a no-payload creator and every
// `dispatch(getNewOrderData({ data }))` in the apps stops compiling.
const requesting = (state: SharedMyOrderState, action: PayloadAction<any>) => {};
const received = (key: StoreKey) => (state: SharedMyOrderState, action: PayloadAction<any>) => {
  state[key] = action.payload;
};

/** Run by both platforms. */
export const sharedReducers = {
  getOverviewOrderData: requesting,
  getOverviewOrderDataSuccess: received("overviewOrdersInfo"),
  getNewOrderData: requesting,
  getNewOrderDataSuccess: received("newOrdersInfo"),
  getPreparingOrderData: requesting,
  getPreparingOrderDataSuccess: received("preparingOrdersInfo"),
  getProcessedOrderData: requesting,
  getProcessedOrderDataSuccess: received("processedOrdersInfo"),
  getDispatchedOrderData: requesting,
  getDispatchedOrderDataSuccess: received("dispatchOrdersInfo"),
  getDeliveredOrderData: requesting,
  getDeliveredOrderDataSuccess: received("deliveredOrdersInfo"),
  getCancelledOrderData: requesting,
  getCancelledOrderDataSuccess: received("cancelledOrdersInfo"),
  getOpenOrderData: requesting,
  getOpenOrderDataSuccess: received("openOrdersInfo"),
  // D1: web's body — the overview is NOT cleared. Both apps dispatch this after a status update.
  resetMyOrders: (state: SharedMyOrderState) => {
    state.newOrdersInfo = {};
    state.preparingOrdersInfo = {};
    state.dispatchOrdersInfo = {};
    state.openOrdersInfo = {};
    state.cancelledOrdersInfo = {};
    state.deliveredOrdersInfo = {};
    state.searchOrdersInfo = {};
  },
  // `isSearchingOrdersFetching` (set here, cleared on success) retired 2026-09-17: no reader on
  // either platform; v2's search spinner is the derived slice's bucket (plan §10.6 row 13).
  getSearchOrders: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  getSearchOrdersSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.searchOrdersInfo = action.payload;
  },
  // No `action` param in either app — RTK generates a no-payload creator. Keep it that way.
  resetSearchOrders: (state: SharedMyOrderState) => {
    state.searchOrdersInfo = {};
  },
  updateOrderStatus: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isOrderStatusUpdate = true;
  },
  updateOrderStatusSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isOrderStatusUpdate = false;
  },
  updateFulfillmentStatus: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  fetchOrderData: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingOrderData = true;
  },
  updateSoldOutItem: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isItemSoldOut = true;
  },
  updateSoldOutSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isItemSoldOut = false;
  },
  aggregatorDataUpdate: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isAggregatorUpdate = true;
  },
  aggregatorDataUpdateSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isAggregatorUpdate = false;
  },
  fetchHoldOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingHoldOrder = true;
  },
  fetchHoldOrderSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingHoldOrder = false;
  },
  getCallBtnApi: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingCallBtn = true;
  },
  getCallBtnApiSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingCallBtn = false;
  },
  saveMyorder: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isMyOrderSave = true;
  },
  saveMyorderSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isMyOrderSave = false;
  },
  voidOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  unvoidOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  uncancelledOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  swiggyCallBtnApi: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingCallBtn = true;
  },
  swiggyCallBtnSucess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingCallBtn = false;
  },
  fetchFeebackLink: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  fetchFeebackLinkSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.feedbackInfo = action.payload;
  },
  checkDeliveryPartner: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  // Both apps spell this param `stat`. Unused and positional, so the name carries nothing.
  assignRider: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  cancelRider: (state: SharedMyOrderState, action: PayloadAction<any>) => {},

  // ── Adopted from web ────────────────────────────────────────────────────────────────────
  // Was `index.desktop.ts`-only. Pure state manipulation, no platform API, so mobile can run
  // every one of them. Bodies are web's, verbatim.

  // Web additionally stores the payload as `currentOrderData`; mobile previously discarded it.
  fetchOrderDataSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.isFetchingOrderData = false;
    state.currentOrderData = action.payload;
  },
  getPreparedOrderData: requesting,
  getPreparedOrderDataSuccess: received("preparedOrdersInfo"),
  generateAggregatorBill: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  fetchApprovalStatus: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  // ⚠️ REPLACES the whole slice state with the payload. On mobile that would also drop
  // `isAutoRefreshModeEnabled`, which lives only in the native variant. Nothing on mobile
  // dispatches this today; if something starts to, it must send a mobile-shaped payload.
  updateMyOrderSlice: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    return action.payload;
  },
  getCustomerMaskedNumber: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  cancelOnlineOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  getTotalSaleReturnAmount: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  checkForZomatoChatSupport: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  saveZomatoChatSupportList: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.chatSupportAccess = [...state.chatSupportAccess, action.payload];
  },
  getNewChatNotificationDetails: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    const listOfOrders = { ...state.newChatSupportMessage };
    if (listOfOrders?.[action?.payload]) {
      listOfOrders[action.payload] += 1;
    } else {
      listOfOrders[action.payload] = 1;
    }
    state.newChatSupportMessage = listOfOrders;
  },
  replaceChatSupportMessage: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.newChatSupportMessage = action.payload;
  },
  getChatSupportUrl: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
  getChatSupportUrlSuccess: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.chatSupportUrlDetails = [...state.chatSupportUrlDetails, action.payload];
  },
  replaceChatSupportData: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.chatSupportUrlDetails = action.payload;
  },
  removedChatSupportId: (state: SharedMyOrderState, action: PayloadAction<any>) => {
    state.removedChatSupportId = action.payload;
  },
  getItemImageForOnlineOrder: (state: SharedMyOrderState, action: PayloadAction<any>) => {},
};
